import React, { useState, useEffect, useCallback } from 'react';
import { Button, Row, Col, Form, Badge, Alert, Spinner, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Bot, Plus, Trash2, X, Pencil, RefreshCw, Info, AtSign, ShieldCheck, Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { useSettings } from '../../../../context/SettingsContext';
import InfoHint from '../../SharedComponents/InfoHint';
import SettingsSelect from '../SettingsSelect';
import ConfirmModal from '../../Common/ConfirmModal';

// Self-serve custom agents — STRUCTURED authoring (Phase 5, plan §10.1). The form captures
// WHAT the agent is for (ansvar, verktyg, leveranser, eskalering, minnespolicy, rutiner);
// the platform compiles that deterministically — raw instruction text survives only as the
// "Avancerat" field. Saving goes through an ACTIVATION REVIEW computed server-side from the
// same clamps that run at build time, so what you approve is what will actually run.
// The platform owns every boundary: permitted tool catalog, human approval on every write,
// model allowlist — the UI only makes them visible.

const BUILT_IN_AGENTS = [
  { name: 'accounting', label: 'Harper (bokföring)' },
  { name: 'ap', label: 'Paige (leverantörsfakturor)' },
  { name: 'ar', label: 'Reece (kundfakturor)' },
  { name: 'payroll', label: 'Cruz (lön)' },
  { name: 'reconciliation', label: 'Mats (bankavstämning)' },
  { name: 'advisory', label: 'Sage (rådgivning)' },
  { name: 'data-analyst', label: 'Quinn (dataanalys)' },
];

const emptyDraft = () => ({
  name: '',
  title: '',
  description: '',
  persona: { displayName: '', handle: '' },
  responsibility: '',
  outputArtifacts: '',
  escalationTargets: [],
  memoryPolicy: { canReadCompanyMemory: true, canReadUserPreferences: false, mayProposeKinds: ['fact'] },
  selectedSkills: [],
  requestedTools: { read: [], write: [] },
  advanced: '',
  modelPolicy: 'tenant-default',
  model: '',
});

const FALLBACK_MODELS = ['claude-sonnet-4-6', 'claude-opus-4-8', 'claude-haiku-4-5-20251001'];

// The platform's validator speaks API-English (it serves machines too); the banner speaks
// Swedish (F7). Only the common cases are translated — the raw text is the fallback.
const humanizeError = (e) => {
  if (/name must match/.test(e)) return 'Namnet (identifieraren) får bara innehålla små bokstäver, siffror och bindestreck.';
  if (/persona\.handle/.test(e)) return 'Handtaget skrivs med små bokstäver, t.ex. @fakturahjalpen.';
  if (/title is required/.test(e)) return 'Titel saknas (max 80 tecken).';
  if (/description is required/.test(e)) return 'Beskrivning saknas (max 400 tecken).';
  if (/responsibility is required/.test(e)) return 'Ansvar saknas (max 400 tecken).';
  if (/is already used|reserved/.test(e)) return 'Namnet eller handtaget används redan — välj ett annat.';
  return e;
};

const CustomAgentsSection = () => {
  const { companyId } = useSettings();
  const [removeIndex, setRemoveIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agents, setAgents] = useState([]);
  const [catalog, setCatalog] = useState({ read: [], write: [], models: [] });
  const [skills, setSkills] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | index
  const [draft, setDraft] = useState(emptyDraft());
  const [errors, setErrors] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  // The conversational drafter ("beskriv med egna ord") — 404 from the platform = feature off.
  const [describeText, setDescribeText] = useState('');
  const [drafting, setDrafting] = useState(false);
  // The activation review returned by /preview — the modal the save flow runs through.
  const [review, setReview] = useState(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setLoadError('');
    try {
      const { data } = await axios.get(getApiUrl(`${companyId}/custom-agents`), { withCredentials: true });
      setAgents(Array.isArray(data?.customAgents) ? data.customAgents : []);
      setCatalog(data?.catalog || { read: [], write: [], models: [] });
    } catch (err) {
      setLoadError(
        err?.response?.status === 403
          ? 'Egna agenter ingår inte i din nuvarande plan.'
          : 'Kunde inte ladda egna agenter.'
      );
    } finally {
      setLoading(false);
    }
    try {
      const { data } = await axios.get(getApiUrl(`${companyId}/agent-skills`), { withCredentials: true });
      setSkills(Array.isArray(data?.system) ? data.system.map((s) => s.name).filter(Boolean) : []);
    } catch {
      setSkills([]);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const models = (catalog.models && catalog.models.length > 0) ? catalog.models : FALLBACK_MODELS;

  const startNew = () => {
    setErrors([]);
    setDraft(emptyDraft());
    setShowAdvanced(false);
    setDescribeText('');
    setEditing('new');
  };

  // Editing any stored row — v3 rows round-trip; v1/v2 rows migrate: their raw text lands in
  // the ADVANCED field and the structured basics are filled in by the human (validation
  // requires ansvar, so a legacy row cannot be re-saved without gaining structure).
  const startEdit = (i) => {
    const a = agents[i] || {};
    const s = a.structured || {};
    setErrors([]);
    setDraft({
      name: a.name || '',
      title: a.title || '',
      description: a.description || '',
      persona: { displayName: a.persona?.displayName || '', handle: a.persona?.handle || '' },
      responsibility: s.responsibility || '',
      outputArtifacts: (s.outputArtifacts || []).join(', '),
      escalationTargets: s.escalationTargets || [],
      memoryPolicy: s.memoryPolicy || { canReadCompanyMemory: true, canReadUserPreferences: false, mayProposeKinds: ['fact'] },
      selectedSkills: s.selectedSkills || [],
      requestedTools: {
        read: s.readCapabilities || a.requestedTools?.read || [],
        write: s.proposedActions || a.requestedTools?.write || [],
      },
      advanced: a.schemaVersion === 3 ? (a.tenantInstructions || '') : (a.tenantInstructions || a.systemPrompt || ''),
      modelPolicy: a.modelSelection?.policy === 'explicit-allowlisted' || a.model ? 'explicit' : 'tenant-default',
      model: a.modelSelection?.model || a.model || '',
    });
    setShowAdvanced(Boolean(a.systemPrompt || a.tenantInstructions));
    setEditing(i);
  };

  const cancel = () => { setEditing(null); setErrors([]); setReview(null); };

  const setField = (field, value) => setDraft((d) => ({ ...d, [field]: value }));
  // F7: users type "Fakturahjälpen" — the slug is OUR problem. Derived from the display
  // name/title while creating, until the user edits the slug field themselves.
  const [slugTouched, setSlugTouched] = useState(false);
  const slugify = (v) => String(v).toLowerCase()
    .replace(/[åä]/g, 'a').replace(/ö/g, 'o').replace(/é/g, 'e')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').replace(/^[^a-z]+/, '').slice(0, 40);
  const setPersona = (field, value) => setDraft((d) => {
    const next = { ...d, persona: { ...d.persona, [field]: value } };
    if (field === 'displayName' && editing === 'new' && !slugTouched) next.name = slugify(value);
    return next;
  });
  const setPolicy = (field, value) => setDraft((d) => ({ ...d, memoryPolicy: { ...d.memoryPolicy, [field]: value } }));
  const toggleIn = (field, name) => setDraft((d) => {
    const list = d[field];
    return { ...d, [field]: list.includes(name) ? list.filter((t) => t !== name) : [...list, name] };
  });
  const toggleTool = (kind, name) => setDraft((d) => {
    const list = d.requestedTools[kind];
    const next = list.includes(name) ? list.filter((t) => t !== name) : [...list, name];
    return { ...d, requestedTools: { ...d.requestedTools, [kind]: next } };
  });

  /** The schema-3 row exactly as the platform stores it. */
  const buildAgent = () => {
    const outputArtifacts = draft.outputArtifacts.split(',').map((a) => a.trim()).filter(Boolean);
    const agent = {
      schemaVersion: 3,
      name: draft.name.trim(),
      title: draft.title.trim(),
      description: draft.description.trim(),
      structured: {
        responsibility: draft.responsibility.trim(),
        invocation: { manual: true, mentions: true },
        readCapabilities: draft.requestedTools.read,
        proposedActions: draft.requestedTools.write,
        ...(outputArtifacts.length > 0 && { outputArtifacts }),
        ...(draft.escalationTargets.length > 0 && { escalationTargets: draft.escalationTargets }),
        memoryPolicy: draft.memoryPolicy,
        ...(draft.selectedSkills.length > 0 && { selectedSkills: draft.selectedSkills }),
      },
      ...(draft.advanced.trim() && { tenantInstructions: draft.advanced }),
      modelSelection:
        draft.modelPolicy === 'explicit' && draft.model
          ? { policy: 'explicit-allowlisted', model: draft.model }
          : { policy: 'tenant-default' },
    };
    const dn = draft.persona.displayName.trim();
    const handle = draft.persona.handle.trim();
    if (dn || handle) agent.persona = { displayName: dn, handle };
    return agent;
  };

  /** "Beskriv med egna ord" → a DRAFT that prefills the form. Nothing is saved. */
  const requestDraft = async () => {
    setDrafting(true);
    try {
      const { data } = await axios.post(
        getApiUrl(`${companyId}/custom-agents/draft`),
        { description: describeText.trim() },
        { withCredentials: true, timeout: 70000 },
      );
      if (data?.mode === 'draft' && data.draft) {
        const a = data.draft;
        const s = a.structured || {};
        setDraft((d) => ({
          ...d,
          name: a.name || d.name,
          title: a.title || d.title,
          description: a.description || d.description,
          persona: a.persona ? { displayName: a.persona.displayName || '', handle: a.persona.handle || '' } : d.persona,
          responsibility: s.responsibility || d.responsibility,
          outputArtifacts: (s.outputArtifacts || []).join(', '),
          escalationTargets: s.escalationTargets || [],
          memoryPolicy: s.memoryPolicy || d.memoryPolicy,
          selectedSkills: s.selectedSkills || [],
          requestedTools: { read: s.readCapabilities || [], write: s.proposedActions || [] },
        }));
        const dropped = [...(data.droppedTools || []), ...(data.droppedSkills || []), ...(data.droppedEscalations || [])];
        toast.success(dropped.length > 0
          ? `Utkast klart — ${dropped.length} förslag utanför katalogen togs bort (${dropped.join(', ')}).`
          : 'Utkast klart — granska och justera innan du sparar.');
      } else if (data?.mode === 'denied') {
        toast.error('Agentbyggaren är inte tillgänglig just nu (krediter eller plan).');
      } else {
        toast.error('Kunde inte ta fram ett utkast — fyll i formuläret manuellt.');
      }
    } catch (err) {
      if (err?.response?.status === 404) {
      } else {
        toast.error('Kunde inte ta fram ett utkast.');
      }
    } finally {
      setDrafting(false);
    }
  };

  const [fieldErrors, setFieldErrors] = useState({});

  /** Friendly, field-anchored checks BEFORE the platform round-trip (F7). */
  const validateFields = () => {
    const fe = {};
    if (!/^[a-z][a-z0-9-]{1,39}$/.test(draft.name)) {
      fe.name = 'Ange ett kort namn med små bokstäver och bindestreck, t.ex. "fakturahjalpen".';
    }
    if (!draft.title.trim()) fe.title = 'Ange en titel.';
    if (!draft.description.trim()) fe.description = 'Beskriv när agenten ska anlitas — Finn använder texten för att välja rätt agent.';
    if (!draft.responsibility.trim()) fe.responsibility = 'Beskriv agentens ansvar i 1–2 meningar.';
    if (draft.persona.handle && !/^@?[a-z0-9][a-z0-9-]{1,30}$/.test(draft.persona.handle.toLowerCase())) {
      fe.handle = 'Handtaget skrivs med små bokstäver, t.ex. "fakturahjalpen".';
    }
    setFieldErrors(fe);
    return Object.keys(fe).length === 0;
  };

  /** Save = preview (validation + activation review) → the review modal → confirmed PUT. */
  const reviewAndSave = async () => {
    if (!validateFields()) return;
    setSaving(true);
    setErrors([]);
    try {
      const { data } = await axios.post(
        getApiUrl(`${companyId}/custom-agents/preview`),
        buildAgent(),
        { withCredentials: true },
      );
      if (Array.isArray(data?.errors) && data.errors.length > 0) {
        setErrors(data.errors.map(humanizeError));
        return;
      }
      setReview(data?.review || null);
    } catch {
      setErrors(['Kunde inte förhandsgranska. Försök igen.']);
    } finally {
      setSaving(false);
    }
  };

  const confirmSave = async () => {
    setSaving(true);
    try {
      const agent = buildAgent();
      const next = [...agents];
      if (editing === 'new') next.push(agent);
      else next[editing] = agent;
      await axios.put(getApiUrl(`${companyId}/custom-agents`), { customAgents: next }, { withCredentials: true });
      setReview(null);
      setEditing(null);
      toast.success('Agenten är sparad och ingår nu i teamet.');
      await load();
    } catch (err) {
      setReview(null);
      const data = err?.response?.data;
      setErrors(Array.isArray(data?.errors) ? data.errors : ['Kunde inte spara. Försök igen.']);
    } finally {
      setSaving(false);
    }
  };

  const confirmRemoveAgent = async () => {
    setSaving(true);
    try {
      await axios.put(
        getApiUrl(`${companyId}/custom-agents`),
        { customAgents: agents.filter((_, idx) => idx !== removeIndex) },
        { withCredentials: true },
      );
      await load();
    } catch {
      toast.error('Kunde inte ta bort agenten.');
    } finally {
      setSaving(false);
      setRemoveIndex(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <RefreshCw className="spin" size={24} />
        <p className="text-muted mt-2">Laddar egna agenter...</p>
      </div>
    );
  }

  const label = { fontSize: '0.85rem', fontWeight: 500 };
  const hint = { fontSize: '0.72rem' };

  return (
    <>
      {/* Hero */}
      <div className="modern-card" style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)', border: '1px solid #cdeede' }}>
        <div className="modern-card-body d-flex align-items-center justify-content-between" style={{ padding: '24px 28px' }}>
          <div className="d-flex align-items-center gap-3">
            <div style={{ width: 52, height: 52, borderRadius: 14, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
              <Bot size={26} />
            </div>
            <div>
              <h2 className="mb-1" style={{ fontSize: '1.35rem', fontWeight: 600 }}>Egna agenter</h2>
              <p className="mb-0 text-muted" style={{ fontSize: '0.9rem' }}>
                Beskriv vad agenten ansvarar för — instruktionen byggs åt dig. Ingen prompt behövs.
              </p>
            </div>
          </div>
          {editing === null && (
            <Button variant="primary" onClick={startNew} style={{ minWidth: 130 }}>
              <Plus className="me-1" size={16} /> Ny agent
            </Button>
          )}
        </div>
      </div>

      {/* Ceiling notice — the enforced boundary, made visible */}
      <Alert variant="light" className="mt-3 d-flex align-items-start gap-2" style={{ border: '1px solid #e5e7eb', background: '#fafafa' }}>
        <ShieldCheck size={18} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: '0.82rem', color: '#4b5563' }}>
          Egna agenter kan bara välja verktyg från en tillåten katalog, och <strong>varje åtgärd som ändrar data kräver alltid mänskligt godkännande</strong>.
          Behörighetsnivån kan inte sänkas — det är kodtvingat i plattformen.
        </div>
      </Alert>

      {loadError && <Alert variant="warning" className="mt-2">{loadError}</Alert>}

      {/* Editor */}
      {editing !== null && (
        <div className="modern-card mt-3">
          <div className="modern-card-body" style={{ padding: '24px 28px' }}>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="mb-0" style={{ fontSize: '1rem', fontWeight: 600 }}>
                {editing === 'new' ? 'Ny agent' : `Redigera ${draft.name}`}
              </h5>
              <Button variant="link" className="text-muted p-0" onClick={cancel}><X size={20} /></Button>
            </div>

            {errors.length > 0 && (
              <Alert variant="danger">
                <ul className="mb-0" style={{ paddingLeft: 18 }}>
                  {errors.map((e, i) => <li key={i} style={{ fontSize: '0.82rem' }}>{e}</li>)}
                </ul>
              </Alert>
            )}

            {/* "Beskriv med egna ord" — the gated drafter. Draft only; the form stays the truth. */}
            {editing === 'new' && (
              <div className="mb-3 p-3 rounded" style={{ background: 'var(--rgba-primary-1)' }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Sparkles size={15} style={{ color: 'var(--primary)' }} />
                  <span style={label}>Beskriv med egna ord (valfritt)</span>
                </div>
                <div className="d-flex gap-2">
                  <Form.Control
                    value={describeText}
                    onChange={(e) => setDescribeText(e.target.value)}
                    placeholder={'T.ex. "En agent som bevakar våra leverantörsavtal och flaggar prisökningar."'}
                  />
                  <Button variant="outline-primary" onClick={requestDraft} disabled={drafting || !describeText.trim()}>
                    {drafting ? <Spinner size="sm" /> : 'Föreslå utkast'}
                  </Button>
                  <InfoHint text="Utkastet fyller i formuläret — inget sparas förrän du granskat och godkänt." />
                </div>
              </div>
            )}

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label style={label}>
                    Identifierare (slug)
                    <InfoHint text="Fylls i automatiskt från visningsnamnet. Kan inte ändras senare." />
                  </Form.Label>
                  <Form.Control value={draft.name} disabled={editing !== 'new'} placeholder="avtalskollen"
                    isInvalid={Boolean(fieldErrors.name)}
                    onChange={(e) => { setSlugTouched(true); setField('name', slugify(e.target.value) || e.target.value); }} />
                  {fieldErrors.name && (
                    <Form.Control.Feedback type="invalid">{fieldErrors.name}</Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label style={label}>Visningsnamn</Form.Label>
                  <Form.Control value={draft.persona.displayName} placeholder="Alva"
                    onChange={(e) => setPersona('displayName', e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label style={label}>Handtag</Form.Label>
                  <div className="position-relative">
                    <AtSign size={14} style={{ position: 'absolute', left: 10, top: 11, color: '#9ca3af' }} />
                    <Form.Control value={draft.persona.handle} placeholder="alva" isInvalid={Boolean(fieldErrors.handle)}
                      onChange={(e) => setPersona('handle', e.target.value)} style={{ paddingLeft: 28 }} />
                    <Form.Control.Feedback type="invalid">{fieldErrors.handle}</Form.Control.Feedback>
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label style={label}>Titel</Form.Label>
                  <Form.Control value={draft.title} placeholder="Avtalsbevakare" isInvalid={Boolean(fieldErrors.title)}
                    onChange={(e) => setField('title', e.target.value)} />
                  <Form.Control.Feedback type="invalid">{fieldErrors.title}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <div className="d-flex align-items-center gap-1">
                    <Form.Label className="mb-0" style={label}>Beskrivning</Form.Label>
                    <OverlayTrigger placement="top" overlay={<Tooltip>Finn använder beskrivningen för att avgöra när agenten ska anlitas.</Tooltip>}>
                      <Info size={14} style={{ color: '#9ca3af', cursor: 'help' }} />
                    </OverlayTrigger>
                  </div>
                  <Form.Control value={draft.description} placeholder="Bevakar leverantörsavtal och återkommande kostnader." isInvalid={Boolean(fieldErrors.description)}
                    onChange={(e) => setField('description', e.target.value)} style={{ marginTop: 4 }} />
                  <Form.Control.Feedback type="invalid">{fieldErrors.description}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label style={label}>Ansvar</Form.Label>
              <Form.Control as="textarea" rows={2} value={draft.responsibility} isInvalid={Boolean(fieldErrors.responsibility)}
                placeholder="Vad ansvarar agenten för? 1–2 meningar."
                onChange={(e) => setField('responsibility', e.target.value)} />
              <Form.Control.Feedback type="invalid">{fieldErrors.responsibility}</Form.Control.Feedback>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Label style={label}>Läsverktyg</Form.Label>
                <div className="d-flex flex-column gap-1 mb-3" style={{ maxHeight: 170, overflowY: 'auto' }}>
                  {(catalog.read || []).map((t) => (
                    <Form.Check key={t} type="checkbox" id={`read-${t}`} label={t}
                      checked={draft.requestedTools.read.includes(t)} onChange={() => toggleTool('read', t)}
                      style={{ fontSize: '0.82rem' }} />
                  ))}
                </div>
              </Col>
              <Col md={6}>
                <Form.Label style={label}>
                  Åtgärder agenten får föreslå <Badge bg="warning" text="dark" style={{ fontSize: '0.65rem' }}>kräver alltid godkännande</Badge>
                </Form.Label>
                <div className="d-flex flex-column gap-1 mb-3" style={{ maxHeight: 170, overflowY: 'auto' }}>
                  {(catalog.write || []).map((t) => (
                    <Form.Check key={t} type="checkbox" id={`write-${t}`} label={t}
                      checked={draft.requestedTools.write.includes(t)} onChange={() => toggleTool('write', t)}
                      style={{ fontSize: '0.82rem' }} />
                  ))}
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={label}>
                    Leveranser (valfritt)
                    <InfoHint text="Kommaseparerade etiketter för vad agenten tar fram." />
                  </Form.Label>
                  <Form.Control value={draft.outputArtifacts} placeholder="veckorapport, avvikelselista"
                    onChange={(e) => setField('outputArtifacts', e.target.value)} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={label}>Eskalera till (valfritt)</Form.Label>
                  <div className="d-flex flex-column gap-1" style={{ maxHeight: 130, overflowY: 'auto' }}>
                    {BUILT_IN_AGENTS.map((a) => (
                      <Form.Check key={a.name} type="checkbox" id={`esc-${a.name}`} label={a.label}
                        checked={draft.escalationTargets.includes(a.name)}
                        onChange={() => toggleIn('escalationTargets', a.name)} style={{ fontSize: '0.82rem' }} />
                    ))}
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={label}>
                    Minne
                    <InfoHint text="Styr vad agenten får se när minnesfunktionen är aktiv." />
                  </Form.Label>
                  <Form.Check type="switch" id="mem-company" label="Får läsa företagets minnen"
                    checked={draft.memoryPolicy.canReadCompanyMemory}
                    onChange={(e) => setPolicy('canReadCompanyMemory', e.target.checked)} style={{ fontSize: '0.82rem' }} />
                  <Form.Check type="switch" id="mem-user" label="Får läsa användarpreferenser"
                    checked={draft.memoryPolicy.canReadUserPreferences}
                    onChange={(e) => setPolicy('canReadUserPreferences', e.target.checked)} style={{ fontSize: '0.82rem' }} />
                </Form.Group>
                {skills.length > 0 && (
                  <Form.Group className="mb-3">
                    <Form.Label style={label}>Rutiner (verifierade)</Form.Label>
                    <div className="d-flex flex-column gap-1" style={{ maxHeight: 130, overflowY: 'auto' }}>
                      {skills.map((sk) => (
                        <Form.Check key={sk} type="checkbox" id={`skill-${sk}`}
                          label={<span>{sk} <Badge bg="success" style={{ fontSize: '0.6rem' }}>verifierad</Badge></span>}
                          checked={draft.selectedSkills.includes(sk)}
                          onChange={() => toggleIn('selectedSkills', sk)} style={{ fontSize: '0.82rem' }} />
                      ))}
                    </div>
                  </Form.Group>
                )}
              </Col>
            </Row>

            {/* Advanced: model + raw addendum. Deliberately folded away — the plan's "raw prompt
                text is optional/advanced" criterion, verbatim. */}
            <button type="button" className="btn btn-link p-0 mb-2 text-muted" style={{ fontSize: '0.82rem', textDecoration: 'none' }}
              onClick={() => setShowAdvanced((v) => !v)}>
              {showAdvanced ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Avancerat
            </button>
            {showAdvanced && (
              <div className="mb-3 p-3 rounded" style={{ background: '#fafafa', border: '1px solid #f3f4f6' }}>
                <Row>
                  <Col md={5}>
                    <Form.Group className="mb-3">
                      <Form.Label style={label}>Modell</Form.Label>
                      <SettingsSelect
                        options={[
                          { value: '', label: 'Plattformens standard (rekommenderas)' },
                          ...models.map((m) => ({ value: m, label: m })),
                        ]}
                        value={draft.modelPolicy === 'explicit' ? draft.model : ''}
                        onChange={(value) => setDraft((d) => ({
                          ...d,
                          modelPolicy: value ? 'explicit' : 'tenant-default',
                          model: value,
                        }))}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group>
                  <Form.Label style={label}>Egna instruktioner (tillägg)</Form.Label>
                  <Form.Control as="textarea" rows={4} value={draft.advanced}
                    placeholder="Läggs till EFTER den byggda instruktionen — behövs sällan."
                    onChange={(e) => setField('advanced', e.target.value)} />
                </Form.Group>
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button variant="outline-secondary" onClick={cancel} disabled={saving}>Avbryt</Button>
              <Button variant="primary" onClick={reviewAndSave} disabled={saving}>
                {saving ? <Spinner size="sm" className="me-2" /> : <ShieldCheck className="me-2" size={15} />}
                Granska & spara
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      {editing === null && (
        <div className="mt-3">
          {agents.length === 0 && !loadError && (
            <div className="modern-card">
              <div className="modern-card-body text-center text-muted py-5">
                Inga egna agenter än. Skapa din första med <strong>Ny agent</strong>.
              </div>
            </div>
          )}
          {agents.map((a, i) => (
            <div key={a.name || i} className="modern-card mb-2">
              <div className="modern-card-body d-flex align-items-center justify-content-between" style={{ padding: '16px 24px' }}>
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <span style={{ fontWeight: 600 }}>{a.persona?.displayName || a.title || a.name}</span>
                    {a.persona?.handle && <Badge bg="light" text="dark" style={{ fontSize: '0.72rem' }}>@{String(a.persona.handle).replace(/^@/, '')}</Badge>}
                    <span className="text-muted" style={{ fontSize: '0.78rem' }}>{a.title}</span>
                    {a.schemaVersion !== 3 && <Badge bg="secondary" style={{ fontSize: '0.62rem' }}>äldre format</Badge>}
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: 2 }}>
                    {a.structured?.responsibility || a.description}
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.72rem', marginTop: 4 }}>
                    {(a.structured?.readCapabilities?.length ?? a.requestedTools?.read?.length ?? 0)} läs ·{' '}
                    {(a.structured?.proposedActions?.length ?? a.requestedTools?.write?.length ?? 0)} åtgärder (HITL)
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <Button variant="outline-secondary" size="sm" onClick={() => startEdit(i)}><Pencil size={14} /></Button>
                  <Button variant="outline-danger" size="sm" onClick={() => setRemoveIndex(i)} disabled={saving}><Trash2 size={14} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* The activation review (F8: redesigned) — what the agent will ACTUALLY be able to do,
          from the platform's own clamps. Confirming here is the save. */}
      <Modal show={review !== null} onHide={() => setReview(null)} size="lg" centered>
        <Modal.Header closeButton style={{ borderBottom: 'none', paddingBottom: 0 }}>
          <Modal.Title style={{ fontSize: '0.95rem', fontWeight: 500, color: '#6b7280' }}>Granska innan aktivering</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '0.86rem', paddingTop: 8 }}>
          {review && (
            <>
              {/* Identity */}
              <div className="d-flex align-items-center gap-3 mb-3 p-3 rounded" style={{ background: 'var(--rgba-primary-1)' }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.1rem', flexShrink: 0 }}>
                  {String(review.displayName || '?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '1rem' }}>{review.displayName}</div>
                  <div className="text-muted" style={{ fontSize: '0.78rem' }}>@{review.name} · svarar i chatten som en del av Finns team</div>
                </div>
              </div>

              {(review.tools.droppedRead.length > 0 || review.tools.droppedWrite.length > 0 || review.skills.unknown.length > 0) && (
                <Alert variant="warning" className="py-2" style={{ fontSize: '0.8rem' }}>
                  Utanför det tillåtna och därför borttaget:{' '}
                  {[...review.tools.droppedRead, ...review.tools.droppedWrite, ...review.skills.unknown].join(', ')}
                </Alert>
              )}

              {/* Capabilities */}
              <Row className="mb-2">
                <Col md={6} className="mb-3">
                  <div className="small fw-semibold text-muted mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.68rem' }}>Läser</div>
                  {review.tools.read.length === 0 ? (
                    <span className="text-muted small">Inga läsverktyg</span>
                  ) : (
                    <div className="d-flex flex-wrap gap-1">
                      {review.tools.read.map((t) => (
                        <Badge key={t} bg="light" text="dark" style={{ fontSize: '0.7rem', fontWeight: 500, border: '1px solid #e5e7eb' }}>{t}</Badge>
                      ))}
                    </div>
                  )}
                </Col>
                <Col md={6} className="mb-3">
                  <div className="small fw-semibold text-muted mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.68rem' }}>Föreslår åtgärder</div>
                  {review.approvals.length === 0 ? (
                    <span className="text-muted small">Inga skrivande åtgärder</span>
                  ) : (
                    <div className="d-flex flex-column gap-1">
                      {review.approvals.map((ap) => (
                        <div key={ap.tool} className="d-flex align-items-center justify-content-between gap-2 px-2 py-1 rounded" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
                          <code style={{ fontSize: '0.74rem' }}>{ap.tool}</code>
                          <span style={{ fontSize: '0.68rem', fontWeight: 600, color: '#92400e' }}>
                            {ap.tier === 'needs-owner' ? 'KRÄVER ÄGARE' : 'KRÄVER GODKÄNNANDE'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </Col>
              </Row>

              {/* Facts row */}
              <Row className="mb-2">
                <Col md={4} className="mb-2">
                  <div className="small fw-semibold text-muted mb-1" style={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.68rem' }}>Minne</div>
                  <div style={{ fontSize: '0.8rem' }}>
                    Företagsminnen: <strong>{review.memoryPolicy?.canReadCompanyMemory ? 'Ja' : 'Nej'}</strong><br />
                    Preferenser: <strong>{review.memoryPolicy?.canReadUserPreferences ? 'Ja' : 'Nej'}</strong><br />
                    {review.memoryProposalPolicyStatus === 'reserved-unimplemented' && (
                      <span className="text-muted">Minnesförslag från egna agenter: reserverat, inte implementerat</span>
                    )}
                  </div>
                </Col>
                <Col md={4} className="mb-2">
                  <div className="small fw-semibold text-muted mb-1" style={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.68rem' }}>Rutiner</div>
                  {review.skills.verified.length === 0 ? (
                    <span className="text-muted" style={{ fontSize: '0.8rem' }}>Inga</span>
                  ) : (
                    <div className="d-flex flex-wrap gap-1">
                      {review.skills.verified.map((sk) => (
                        <Badge key={sk} bg="success" style={{ fontSize: '0.66rem' }}>{sk}</Badge>
                      ))}
                    </div>
                  )}
                </Col>
                <Col md={4} className="mb-2">
                  <div className="small fw-semibold text-muted mb-1" style={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.68rem' }}>Modell</div>
                  <span style={{ fontSize: '0.8rem' }}>{review.model.model}</span>
                  {review.model.fallbackReason && (
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>plattformens val</div>
                  )}
                </Col>
              </Row>

              <details className="mb-2">
                <summary className="small text-muted" style={{ cursor: 'pointer' }}>Visa den byggda instruktionen</summary>
                <pre className="mt-2 p-2 rounded" style={{ background: '#fafafa', whiteSpace: 'pre-wrap', fontSize: '0.75rem', border: '1px solid #f3f4f6', maxHeight: 200, overflowY: 'auto' }}>{review.tenantInstructions}</pre>
              </details>

              <div className="d-flex align-items-start gap-2 p-2 rounded" style={{ background: '#fafafa', border: '1px solid #f3f4f6' }}>
                <ShieldCheck size={16} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: 2 }} />
                <div className="text-muted" style={{ fontSize: '0.72rem', lineHeight: 1.5 }}>
                  Varje skrivande åtgärd kräver alltid mänskligt godkännande. Agentens instruktioner är
                  företagets egna rutiner — aldrig plattforms- eller myndighetsauktoritet. Verktygskatalogen
                  är plattformens tak, och text kan aldrig ge behörighet.
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: 'none' }}>
          <Button variant="outline-secondary" onClick={() => setReview(null)} disabled={saving}>Tillbaka</Button>
          <Button variant="primary" onClick={confirmSave} disabled={saving}>
            {saving ? <Spinner size="sm" className="me-2" /> : <ShieldCheck size={15} className="me-2" />} Aktivera agenten
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={removeIndex !== null}
        title="Ta bort agenten?"
        body="Agenten tas bort från din roster. Redan utförda åtgärder påverkas inte."
        detail={removeIndex !== null ? agents[removeIndex]?.name : null}
        confirmLabel="Ta bort agent"
        onConfirm={confirmRemoveAgent}
        onCancel={() => setRemoveIndex(null)}
      />
    </>
  );
};

export default CustomAgentsSection;
